import { Button, Link, NavLink } from "@liberetech/design-system";
import { useRouter } from "next/router";

import CardKlee from "components/CardKlee/CardKlee";
import CardKleeActions from "components/CardKlee/CardKleeActions";
import CardKleeBanner from "components/CardKlee/CardKleeBanner";
import CardKleeFeaturedImage from "components/CardKlee/CardKleeFeaturedImage";
import Carousel from "components/Carousel/Carousel";
import stylesCarousel from "components/Carousel/CarouselCardImage.module.css";
import { useBrand } from "components/Context/BrandContext";
import {
  useFeatureIsOn,
  useFeaturesReady,
} from "components/Context/ExperimentContext";
import { useSearchBar } from "components/Context/PageContext";
import { useSession } from "components/Context/SessionContext";
import ImageCDN from "components/ImageCDN/ImageCDN";
import NextLinkButton from "components/NextLinkButton/NextLinkButton";
import { ButtonSkeleton } from "components/Skeleton/Skeleton";
import { disallowedVoucherCodesForFeedback } from "lib/book/book";
import { Trans, useTranslation } from "lib/i18n/client";
import PricePerNight, {
  PricePerNightSkeleton,
} from "pagesComponents/_shared/PricePerNight/PricePerNight";
import SpaceCategoryFeatures from "pagesComponents/_shared/SpaceCategoryFeatures/SpaceCategoryFeatures";

import {
  AssetHomeCardSpaceAvailableRateFragment,
  AssetHomeCardSpaceSpaceCategoryFragment,
} from "./AssetHomeCardSpace.generated";
import styles from "./AssetHomeCardSpace.module.css";

const AssetHomeCardSpace: React.FC<AssetHomeCardSpaceProps> = ({
  spaceCategory,
  onContactClicked,
  asset,
  rate,
  isRateLoading,
  isViewerInCommunity,
  alternativeDestination,
}) => {
  const { t } = useTranslation("asset");
  const { query, locale } = useRouter();
  const searchBar = useSearchBar();
  const session = useSession();
  const brand = useBrand();

  const { from, to, voucher } = query;
  const hasNoAvailability =
    !isRateLoading && !!query.from && !!query.to && !rate?.perNightPrice;
  const isAssetClosed = asset.properties.closed;
  const isMidStayStatic = asset.properties.stayType === "mid_stay_static";
  const isCommunityApplicable =
    brand.id === "libere" &&
    (!session.session?.accountId ||
      session.session?.accountType === "INHABITANT");
  const isVoucherCodeNotApplied = !!(
    !isRateLoading &&
    !hasNoAvailability &&
    voucher &&
    !disallowedVoucherCodesForFeedback.includes(
      (voucher as string).toLocaleUpperCase(),
    ) &&
    !rate?.enabledByVoucherCode
  );

  const onSearchOtherDatesClicked = () => {
    searchBar.open();
    window.dataLayer?.push({
      event: "click",
      event_category: "price_per_night",
      event_label: "change_dates",
    });
  };
  const mostRelevantMessageKey = mostRelevantMessage(
    !!(query.to && query.from),
    hasNoAvailability,
    isViewerInCommunity,
    isVoucherCodeNotApplied,
    isCommunityApplicable,
  );
  const crossOutPricesExperimentEnabled = useFeatureIsOn(
    "show-direct-channel-discount",
  );
  const isFeaturesReady = useFeaturesReady();

  return (
    <CardKlee className={styles.klee}>
      <CardKleeFeaturedImage>
        {spaceCategory.images && spaceCategory.images.length > 0 && (
          <Carousel classNames={stylesCarousel}>
            {spaceCategory.images.map((image, i) => (
              <ImageCDN
                className={stylesCarousel.inner}
                key={i}
                src={image.url}
                alt={spaceCategory.name}
                sizes="(min-width: 1024px) 450px, (min-width: 768px) 50vw, 100vw"
                loading="lazy"
              />
            ))}
          </Carousel>
        )}
      </CardKleeFeaturedImage>
      <div className={styles.kleeContent}>
        <h3 className={styles.kleeTitle}>{spaceCategory.name}</h3>
        <SpaceCategoryFeatures
          assetType={asset.properties.type}
          features={spaceCategory}
        />
        <NavLink href="#amenities">
          <Link>+ {t("spaceCategory.allAmenities")}</Link>
        </NavLink>
      </div>
      {isAssetClosed && (
        <CardKleeBanner type="error">
          <Trans
            i18nKey="price:notAvailable"
            components={{ strong: <strong /> }}
          />
        </CardKleeBanner>
      )}
      {!isAssetClosed && (
        <CardKleeActions noPadding>
          {isMidStayStatic && (
            <>
              <CardKleeBanner type="info">
                {t("spaceCategory.midStayOnly")}
              </CardKleeBanner>
              <div className={styles.kleeFooter}>
                <Button onClick={onContactClicked}>
                  {t("spaceCategory.contact")}
                </Button>
              </div>
            </>
          )}
          {!isMidStayStatic && (
            <>
              {mostRelevantMessageKey === "no_availability" && (
                <CardKleeBanner type="error">
                  <Trans
                    i18nKey={
                      alternativeDestination
                        ? "price:notAvailableOtherAssets"
                        : "price:notAvailableOtherDates"
                    }
                    values={{ city: alternativeDestination?.title }}
                    components={{
                      strong: <strong />,
                      linkDates: (
                        <OpenSearchBarLink
                          onClick={onSearchOtherDatesClicked}
                        />
                      ),
                      linkDestination: (
                        <DestinationLink destination={alternativeDestination} />
                      ),
                    }}
                  />
                </CardKleeBanner>
              )}
              {mostRelevantMessageKey === "voucher_not_applied" && (
                <CardKleeBanner type={"warn"}>
                  <Trans
                    i18nKey={`price:voucherCodeNotApplied`}
                    values={{ voucherCode: voucher }}
                    components={{
                      strong: <strong />,
                      linkDates: (
                        <OpenSearchBarLink
                          onClick={onSearchOtherDatesClicked}
                        />
                      ),
                    }}
                  />
                </CardKleeBanner>
              )}
              {mostRelevantMessageKey === "select_dates" && (
                <>
                  <CardKleeBanner type="info">
                    {t("selectDates.description")}
                  </CardKleeBanner>
                </>
              )}
              {mostRelevantMessageKey === "viewer_not_in_community" && (
                <div className={styles.joinCommunity}>
                  <Trans
                    i18nKey={`communityJoin:joinCommunity${crossOutPricesExperimentEnabled && isFeaturesReady ? "_crossOutPricesExperiment" : ""}`}
                    components={{
                      strong: <strong />,
                      communityLink: <CommunityLink />,
                    }}
                  />
                </div>
              )}
              {!hasNoAvailability && (
                <div className={styles.kleeFooter}>
                  {(!query.to || !query.from) && (
                    <Button
                      onClick={() => {
                        searchBar.open();
                      }}
                    >
                      {t("selectDates.action")}
                    </Button>
                  )}
                  {isRateLoading && (
                    <>
                      <PricePerNightSkeleton />
                      <ButtonSkeleton />
                    </>
                  )}
                  {rate?.perNightPrice && !hasNoAvailability && (
                    <>
                      <PricePerNight
                        minPrice={rate.perNightPrice}
                        originalPrice={rate.originalPerNightPrice}
                        promoName={rate.promoName}
                      />
                      <NextLinkButton
                        className={styles.cardSpaceFooterButton}
                        href={`/${locale}/book?${new URLSearchParams({
                          from: from as string,
                          to: to as string,
                          voucher: voucher as string,
                          asset_id: asset.properties.id,
                          space_category_id: spaceCategory.id,
                        }).toString()}`}
                      >
                        {t("seeRates")}
                      </NextLinkButton>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </CardKleeActions>
      )}
    </CardKlee>
  );
};

const mostRelevantMessage = (
  hasDatesSelected: boolean,
  hasNoAvailability: boolean,
  isViewerInCommunity: boolean,
  isVoucherCodeNotApplied: boolean,
  isCommunityApplicable: boolean,
) => {
  return !hasDatesSelected
    ? "select_dates"
    : hasNoAvailability
      ? "no_availability"
      : isVoucherCodeNotApplied
        ? "voucher_not_applied"
        : !isViewerInCommunity && isCommunityApplicable
          ? "viewer_not_in_community"
          : "";
};

const OpenSearchBarLink: React.FC<{
  onClick: () => void;
}> = ({ children, onClick }) => {
  return (
    <button onClick={onClick}>
      <Link>{children}</Link>
    </button>
  );
};

const CommunityLink: React.FC = ({ children }) => {
  return (
    <NavLink href="/community">
      <Link>{children}</Link>
    </NavLink>
  );
};

const DestinationLink: React.FC<{ destination?: { uri: string } }> = ({
  children,
  destination,
}) => (
  <NavLink
    href={destination?.uri}
    onClick={() => {
      window.dataLayer?.push({
        event: "click",
        event_category: "price_per_night",
        event_label: "change_destination",
      });
    }}
  >
    <Link>{children}</Link>
  </NavLink>
);

type AssetHomeCardSpaceProps = {
  spaceCategory: AssetHomeCardSpaceSpaceCategoryFragment;
  onContactClicked: () => void;
  rate?: AssetHomeCardSpaceAvailableRateFragment | null;
  isRateLoading: boolean;
  isViewerInCommunity: boolean;
  alternativeDestination?: {
    title: string;
    uri: string;
  };
  asset: {
    properties: {
      id: string;
      stayType: string;
      closed: boolean;
      type: string;
    };
    destination: {
      destination?: {
        title: string;
        uri: string;
        assets: Array<{ title: string; uri: string }>;
      };
    };
  };
  voucher?: string;
};

export default AssetHomeCardSpace;
